import { useSyncExternalStore } from 'react';
import {
  BREAKPOINTS,
  HANDSET_MAX_SHORTEST_SIDE,
} from '../constants/breakpoints';

export type LayoutTier = 'phone' | 'tablet' | 'desktop';

export interface LayoutSnapshot {
  tier: LayoutTier;
  isPhone: boolean;
  isTablet: boolean;
  isDesktopTier: boolean;
  /** phone or tablet - the tiers that get the drawer and the compact app bar. */
  isTabletDown: boolean;
  isTabletUp: boolean;
  /** Touch input, not a layout tier. Drives density and popover-versus-sheet. */
  isTouch: boolean;
  width: number;
}

/**
 * Detects touch input from the touch-point, pointer and hover capabilities.
 * Says nothing about how wide the screen is.
 */
function detectTouch(): boolean {
  if (typeof window === 'undefined' || typeof navigator === 'undefined') {
    return false;
  }

  const hasTouch =
    'ontouchstart' in window ||
    navigator.maxTouchPoints > 0 ||
    // Legacy IE/Edge touch points support.
    ((navigator as { msMaxTouchPoints?: number }).msMaxTouchPoints ?? 0) > 0;

  const supportsMatchMedia = typeof window.matchMedia === 'function';
  const hasCoarsePointer = supportsMatchMedia
    ? window.matchMedia('(pointer: coarse)').matches
    : false;
  const hasNoHover = supportsMatchMedia
    ? window.matchMedia('(hover: none)').matches
    : false;

  // iPadOS can report as MacIntel while still being a touch-only device.
  const isIpadOsAsMac = navigator.platform === 'MacIntel' && hasTouch;

  return (hasTouch && (hasCoarsePointer || hasNoHover)) || isIpadOsAsMac;
}

const isTouchDevice = detectTouch();

/**
 * The device's shortest side tells a handset from a tablet, which viewport width
 * alone cannot: an iPhone 13 in landscape is 844px wide but 390px on its shortest
 * side, an iPad Pro 11 in portrait is 834px on its shortest side.
 */
let deviceShortestSide = 0;

function observeShortestSide(): void {
  if (typeof window === 'undefined') return;

  // Grows only. A touch laptop opened in a small window must leave the handset
  // class when it is maximised, and a tablet must never fall into it.
  deviceShortestSide = Math.max(
    deviceShortestSide,
    Math.min(window.innerWidth, window.innerHeight)
  );
}

function computeTier(width: number): LayoutTier {
  // A handset keeps the phone layout in landscape. This is what the old
  // `isMobile || isMobileDevice` protected, without also trapping tablets.
  if (
    isTouchDevice &&
    deviceShortestSide > 0 &&
    deviceShortestSide < HANDSET_MAX_SHORTEST_SIDE
  ) {
    return 'phone';
  }

  if (width < BREAKPOINTS.md) return 'phone';

  // Touch stays on the tablet layout up to xl, so an iPad Pro 11 in landscape
  // (1194px) avoids the data table's hover-only column handles and row actions.
  const desktopFrom = isTouchDevice ? BREAKPOINTS.xl : BREAKPOINTS.lg;
  return width < desktopFrom ? 'tablet' : 'desktop';
}

function buildSnapshot(width: number): LayoutSnapshot {
  const tier = computeTier(width);

  return {
    tier,
    isPhone: tier === 'phone',
    isTablet: tier === 'tablet',
    isDesktopTier: tier === 'desktop',
    isTabletDown: tier !== 'desktop',
    isTabletUp: tier !== 'phone',
    isTouch: isTouchDevice,
    width,
  };
}

const serverSnapshot: LayoutSnapshot = Object.freeze({
  tier: 'desktop',
  isPhone: false,
  isTablet: false,
  isDesktopTier: true,
  isTabletDown: false,
  isTabletUp: true,
  isTouch: false,
  width: BREAKPOINTS.lg,
});

function readWidth(): number {
  return typeof window === 'undefined' ? BREAKPOINTS.lg : window.innerWidth;
}

observeShortestSide();
let snapshot: LayoutSnapshot = buildSnapshot(readWidth());

const listeners = new Set<() => void>();

function refresh(): void {
  observeShortestSide();
  const next = buildSnapshot(readWidth());

  // useSyncExternalStore compares by identity, so only swap on a real change.
  if (next.tier === snapshot.tier && next.width === snapshot.width) {
    return;
  }

  snapshot = next;
  listeners.forEach((listener) => listener());
}

function subscribe(listener: () => void): () => void {
  if (listeners.size === 0 && typeof window !== 'undefined') {
    window.addEventListener('resize', refresh);
    window.addEventListener('orientationchange', refresh);
    window.visualViewport?.addEventListener('resize', refresh);
  }

  listeners.add(listener);
  refresh();

  return () => {
    listeners.delete(listener);

    if (listeners.size === 0 && typeof window !== 'undefined') {
      window.removeEventListener('resize', refresh);
      window.removeEventListener('orientationchange', refresh);
      window.visualViewport?.removeEventListener('resize', refresh);
    }
  };
}

function getSnapshot(): LayoutSnapshot {
  return snapshot;
}

function getServerSnapshot(): LayoutSnapshot {
  return serverSnapshot;
}

/** Resolves the layout tier. One shared subscription serves every caller. */
export function useLayoutTier(): LayoutSnapshot {
  return useSyncExternalStore(subscribe, getSnapshot, getServerSnapshot);
}
